// ============================================================
// frontend/src/views/SettingsView.tsx
// ============================================================
// Therapist defaults (currency, session rate, duration) + logout.
// ============================================================

import { useState } from 'react';
import { LogOut, Sun, Moon } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useTheme, type Theme } from '../lib/theme';
import { useSaving } from '../lib/useSaving';
import { Field } from '../components/UI';
import { SaveButton } from '../components/SaveButton';
import type { Settings, SettingsResponse } from '@consilium/shared';

interface SettingsViewProps {
  settings: Settings | null;
  onSettingsUpdated: (s: Settings) => void;
  showError: (msg: string) => void;
  showSuccess: (msg: string) => void;
}

export function SettingsView({ settings, onSettingsUpdated, showError, showSuccess }: SettingsViewProps) {
  const { therapist, logout } = useAuth();
  const { theme, setTheme } = useTheme();

  // Local form state — initialized from props
  const [data, setData] = useState<Settings>(settings || {
    defaultCurrency: 'PKR', defaultSessionRate: 5000, defaultDurationMin: 50,
  });
  const { saving, saved, run } = useSaving();

  const onSave = async () => {
    try {
      const result = await run(async () => {
        const r = await api.put<SettingsResponse>('/settings', data);
        return r.settings;
      });
      if (result) {
        onSettingsUpdated(result);
        showSuccess('Settings saved');
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save settings');
    }
  };

  return (
    <div className="fade-in">
      <div style={{ marginBottom: 28 }}>
        <div className="mono-cap">Preferences</div>
        <h1 className="page-title">Settings</h1>
      </div>

      <div className="card stack-lg" style={{ marginBottom: 24 }}>
        <div>
          <div className="label">Account</div>
          <div style={{ fontSize: 15, color: 'var(--ink)' }}>{therapist?.name}</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>{therapist?.email}</div>
        </div>
      </div>

      <div className="card stack-lg" style={{ marginBottom: 24 }}>
        <div className="label" style={{ marginBottom: 0 }}>Appearance</div>
        <div className="grid-2" style={{ gap: 8 }}>
          {([
            { id: 'dark' as Theme, label: 'Dark', icon: Moon },
            { id: 'light' as Theme, label: 'Light', icon: Sun },
          ]).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTheme(id)}
              className={`chip ${theme === id ? 'active' : ''}`}
              style={{ justifyContent: 'center', minHeight: 44 }}
              aria-pressed={theme === id}
            >
              <Icon size={15} style={{ marginRight: 7 }} />{label}
            </button>
          ))}
        </div>
      </div>

      <div className="card stack-lg" style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
          Defaults used when creating new packages and sessions. Each can be overridden per-item.
        </div>
        <div className="grid-3">
          <Field label="Currency">
            <select
              className="input" value={data.defaultCurrency}
              onChange={(e) => setData({ ...data, defaultCurrency: e.target.value })}
            >
              <option value="PKR">PKR</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="INR">INR</option>
            </select>
          </Field>
          <Field label="Session rate">
            <input
              type="number" min={0} className="input"
              value={data.defaultSessionRate}
              onChange={(e) => setData({ ...data, defaultSessionRate: Number(e.target.value) })}
            />
          </Field>
          <Field label="Duration (min)">
            <input
              type="number" min={15} step={5} className="input"
              value={data.defaultDurationMin}
              onChange={(e) => setData({ ...data, defaultDurationMin: Number(e.target.value) })}
            />
          </Field>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <SaveButton saving={saving} saved={saved} onClick={onSave}>Save defaults</SaveButton>
        </div>
      </div>

      <div className="card">
        <button
          className="btn-ghost"
          onClick={async () => {
            if (confirm('Sign out?')) await logout();
          }}
        >
          <LogOut size={15} />Sign out
        </button>
      </div>
    </div>
  );
}
